import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BlogPostSkeleton } from '../components/SkeletonLoader';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from 'react-share';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import { processContentUrls, sanitizeContent } from '../utils/contentProcessor';
import BlogAudioPlayer from '../components/BlogAudioPlayer';
import '../styles/pages/BlogPostPage.css';
import '../styles/components/BlogContent.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [post, setPost] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const shareUrl = window.location.href;

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const response = await api.getBlogPost(slug);

      if (response.success && response.data) {
        setPost(response.data);
        fetchRelatedPosts(response.data.category_id, response.data.id);
      } else {
        toast.error('Post not found');
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      toast.error('Failed to load blog post');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (categoryId, currentId) => {
    try {
      const response = await api.getBlogPosts({ category_id: categoryId, limit: 20 });
      if (response.success) {
        const related = response.data
          .filter(p => p.id !== currentId)
          .slice(0, 3);
        setRelatedArticles(related);
      }
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^\S+@\S+$/i.test(subscribeEmail)) {
      toast.error('Invalid email address');
      return;
    }

    try {
      const response = await api.subscribeNewsletter(subscribeEmail);
      if (response.success) {
        toast.success('Successfully subscribed!');
        setSubscribeEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (loading || !post) {
    return <BlogPostSkeleton />;
  }

  const publishDate = new Date(post.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  //Process content to fix image URLs
  const processedContent = processContentUrls(sanitizeContent(post.content));

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "image": post.image,
    "author": {
      "@type": "Person",
      "name": post.author_name || "Supernova Lab"
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at
  };

  return (
    <PageWrapper>
      <SEO
        title={post.meta_title || `${post.title} | Supernova Lab`}
        description={post.meta_description || post.description}
        keywords={post.meta_keywords || post.tags}
        author={post.author_name}
        ogTitle={post.title}
        ogDescription={post.description}
        ogImage={post.image}
        ogUrl={shareUrl}
        type="article"
        schema={schema}
      />

      <main className="blog-post-page">
        <motion.section
          className="blog-post-hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="blog-post-hero-image">
              <SafeImage
                src={post.image}
                alt={post.title}
              />
              <span className="blog-post-category-badge">{post.category_name}</span>
            </div>
          </div>
        </motion.section>

        <section className="blog-post-content-section py-1">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12">
                <motion.article
                  className="blog-post-article"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <h1 className="blog-post-main-title">{post.title}</h1>
                  <div className="blog-divider-post my-4"></div>

                  {/* Audio Player - pass content for fallback */}
                  <BlogAudioPlayer
                    postId={post.id}
                    postTitle={post.title}
                    content={post.content}
                  />

                  {/* Date/Author and Share on Same Line */}
                  <div className="blog-post-meta-share-wrapper">
                    <div className="blog-post-date">
                      By <span className='author-name'>{post.author_name || 'Supernova Lab'}</span>&nbsp;<span>/</span>&nbsp;
                      <span>{publishDate}</span>
                    </div>

                    <div className="share-section-inline">
                      <p className="share-label">Share:</p>
                      <div className="share-buttons">
                        <FacebookShareButton url={shareUrl}>
                          <FacebookIcon size={30} round />
                        </FacebookShareButton>
                        <TwitterShareButton url={shareUrl}>
                          <TwitterIcon size={30} round />
                        </TwitterShareButton>
                        <LinkedinShareButton url={shareUrl}>
                          <LinkedinIcon size={30} round />
                        </LinkedinShareButton>
                        <WhatsappShareButton url={shareUrl}>
                          <WhatsappIcon size={30} round />
                        </WhatsappShareButton>
                        <button onClick={handleCopyLink} className="copy-btn">
                          <i className="fas fa-link"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="blog-divider-post my-4"></div>

                  {/* ✅ Use processed content with fixed URLs */}
                  <div
                    className="blog-post-body mt-4"
                    dangerouslySetInnerHTML={{ __html: processedContent }}
                  />
                </motion.article>

                {relatedArticles.length > 0 && (
                  <div className="related-articles-section mt-5">
                    <h2 className="related-articles-title">Related Articles</h2>

                    <motion.div
                      className="row g-4"
                      variants={containerVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {relatedArticles.map((article) => {
                        const articleDate = new Date(article.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });

                        return (
                          <motion.div key={article.id} className="col-md-4" variants={itemVariants}>
                            <Link to={`/blog/${article.slug}`} className="related-article-card">
                              <div className="related-article-image">
                                <SafeImage src={article.image} alt={article.title} />
                              </div>
                              <div className="related-article-content">
                                <span className="related-article-category">{article.category_name}</span>
                                <h4 className="related-article-title">{article.title}</h4>
                                <p className="related-article-date">{articleDate}</p>
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="newsletter-section-alt">
          <div className="container">
            <div className="text-center">
              <h2 className="newsletter-title-alt mb-3">Stay Informed with Supernova</h2>
              <p className="newsletter-subtitle-alt mb-4">
                Subscribe to our newsletter for the latest updates.
              </p>
              <form onSubmit={handleSubscribe} className="newsletter-form-alt" style={{ width: '100%' }}>
                <input
                  type="email"
                  className="newsletter-input-alt"
                  placeholder="Enter your email"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                />
                <button className="newsletter-btn-alt" type="submit">Subscribe</button>
              </form>
            </div>
          </div>
        </section>
      </main>
    </PageWrapper>
  );
};

export default BlogPostPage;